import { router } from '@inertiajs/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { getDateLocale } from '@/lib/i18n'
import { Button } from '../../components/ui/button'
import { Separator } from '../../components/ui/separator'
import { Badge } from '../../components/ui/badge'
import { Progress } from '../../components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Switch } from '../../components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog'
import { Input } from '../../components/ui/input'
import { CheckIcon, CreditCardIcon, CalendarIcon, Loader2, TicketIcon } from 'lucide-react'
import { toast } from 'sonner'

interface Organization {
  id: number
  name: string
  current_plan: string
  plan_display_name: string
  plan_price: number
  plan_credits: number
  stripe_customer_id: string | null
  credits_used: number
  credits_purchased: number
  credits_total: number
  credits_remaining: number
  usage_reset_at: string | null
  can_purchase_credits: boolean
}

interface Subscription {
  id: number
  status: string
  plan_name: string
  plan_display_name: string
  stripe_price_id: string
  billing_interval: 'month' | 'year'
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  pending_downgrade_price_id: string | null
  pending_downgrade_at: string | null
  pending_downgrade_display_name: string | null
}

interface PlanVariation {
  stripe_price_id: string
  credits: number
  price: number
  monthly_price: number
  display_name: string
  billing_interval: 'month' | 'year'
}

interface TierVariations {
  month: PlanVariation[]
  year: PlanVariation[]
}

type PlanVariations = Record<string, {
  name: string
  description: string
  variations: TierVariations
}>

interface CreditPack {
  credits: number
  price: number
  price_id: string
}

interface ActiveAccessCode {
  code: string
  name: string
  granted_plan: string
  expires_at: string
  bonus_credits_granted: number
}

interface OrganizationSubscriptionProps {
  organization: Organization
  subscription: Subscription | null
  plan_variations: PlanVariations
  credit_packs: CreditPack[]
  active_access_code: ActiveAccessCode | null
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

function Subscription({ organization, subscription, plan_variations, credit_packs, active_access_code }: OrganizationSubscriptionProps) {
  const { t } = useTranslation('organizations')
  const isFreePlan = organization.current_plan === 'free'
  const hasActiveSubscription = subscription && subscription.status === 'active'
  const creditsUsagePercentage = organization.credits_total > 0
    ? (organization.credits_used / organization.credits_total) * 100
    : 0

  // Access code redemption state
  const [accessCode, setAccessCode] = useState('')
  const [isRedeeming, setIsRedeeming] = useState(false)

  const handleRedeemAccessCode = () => {
    if (!accessCode.trim()) return
    setIsRedeeming(true)
    router.post('/organization/redeem_access_code', { code: accessCode }, {
      onFinish: () => {
        setIsRedeeming(false)
        setAccessCode('')
      },
    })
  }

  // State for billing interval toggle (Monthly / Annual)
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(
    subscription?.billing_interval || 'month'
  )

  // Helper to get variations for current billing interval
  const availableTiers = Object.keys(plan_variations)

  const getVariationsForInterval = (tier: string) => {
    return plan_variations[tier]?.variations?.[billingInterval] || []
  }

  // State for selected variations per tier
  // Initialize with current subscription's price if available, otherwise use first variation
  const [selectedVariations, setSelectedVariations] = useState<Record<string, string | undefined>>(() => {
    const currentInterval = subscription?.billing_interval || 'month'
    const initial: Record<string, string | undefined> = {}
    for (const tier of availableTiers) {
      initial[tier] = plan_variations[tier].variations[currentInterval]?.[0]?.stripe_price_id
    }

    // If user has active subscription, set that tier's variation to their current price
    if (subscription && subscription.stripe_price_id) {
      initial[subscription.plan_name] = subscription.stripe_price_id
    }

    return initial
  })

  // Update selected variations when billing interval changes
  const handleBillingIntervalChange = (newInterval: 'month' | 'year') => {
    setBillingInterval(newInterval)

    // Update selected variations to match the same credit level in the new interval
    const newSelections: Record<string, string | undefined> = {}

    for (const tier of availableTiers) {
      const currentPriceId = selectedVariations[tier]
      const oldVariations = plan_variations[tier].variations[billingInterval]
      const newVariations = plan_variations[tier].variations[newInterval]

      // Find the current selection's credits amount
      const currentVariation = oldVariations?.find(v => v.stripe_price_id === currentPriceId)
      const creditsToMatch = currentVariation?.credits

      if (creditsToMatch && newVariations) {
        // Find matching credits in new interval
        const matchingVariation = newVariations.find(v => v.credits === creditsToMatch)
        newSelections[tier] = matchingVariation?.stripe_price_id || newVariations[0]?.stripe_price_id
      } else {
        newSelections[tier] = newVariations?.[0]?.stripe_price_id
      }
    }

    setSelectedVariations(newSelections)
  }

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingTierChange, setPendingTierChange] = useState<string | null>(null)

  // State for cancel subscription dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isReactivating, setIsReactivating] = useState(false)

  // State for credit pack purchase confirmation
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false)
  const [pendingPurchase, setPendingPurchase] = useState<CreditPack | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)

  const handleSubscribe = (tier: string) => {
    const stripePriceId = selectedVariations[tier]
    if (!stripePriceId) return

    // Use a regular form submission to allow external redirects (Stripe Checkout)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/checkout'

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'stripe_price_id'
    input.value = stripePriceId
    form.appendChild(input)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleChangePlan = (tier: string) => {
    const stripePriceId = selectedVariations[tier]
    if (!stripePriceId) return

    // Show confirmation dialog
    setPendingTierChange(tier)
    setShowConfirmDialog(true)
  }

  const confirmChangePlan = () => {
    if (!pendingTierChange) return

    const stripePriceId = selectedVariations[pendingTierChange]
    if (!stripePriceId) return

    // Use Inertia to submit form
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/subscription/update'

    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'stripe_price_id'
    input.value = stripePriceId
    form.appendChild(input)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()

    // Reset dialog state
    setShowConfirmDialog(false)
    setPendingTierChange(null)
  }

  const handleVariationChange = (tier: string, priceId: string) => {
    setSelectedVariations(prev => ({
      ...prev,
      [tier]: priceId
    }))
  }

  // Get selected variation details for a tier
  const getSelectedVariation = (tier: string) => {
    const variations = getVariationsForInterval(tier)
    if (!variations || variations.length === 0) return null

    const selectedPriceId = selectedVariations[tier]
    return variations.find(v => v.stripe_price_id === selectedPriceId) || variations[0]
  }

  // Calculate annual savings (annual = monthly × 10, so savings = monthly × 2)
  const calculateSavings = (tier: string) => {
    const monthlyVariations = plan_variations[tier]?.variations?.month || []
    const selectedPriceId = selectedVariations[tier]

    // Find the matching monthly variation to calculate savings
    const yearlyVariations = plan_variations[tier]?.variations?.year || []
    const selectedYearlyVariation = yearlyVariations.find(v => v.stripe_price_id === selectedPriceId)

    if (selectedYearlyVariation) {
      // Find matching monthly variation by credits
      const matchingMonthly = monthlyVariations.find(v => v.credits === selectedYearlyVariation.credits)
      if (matchingMonthly) {
        // Savings = (monthly price × 12) - yearly price
        return (matchingMonthly.price * 12) - selectedYearlyVariation.price
      }
    }

    // Fallback: use the monthly_price from selected variation
    const selectedVariation = getSelectedVariation(tier)
    if (selectedVariation) {
      return selectedVariation.monthly_price * 2
    }
    return 0
  }

  const handlePurchaseCredits = (creditPack: CreditPack) => {
    setPendingPurchase(creditPack)
    setPurchaseError(null)
    setShowPurchaseDialog(true)
  }

  const confirmPurchaseCredits = async () => {
    if (!pendingPurchase) return

    setIsPurchasing(true)
    setPurchaseError(null)

    try {
      const response = await fetch('/billing/credit_packs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          credits_amount: pendingPurchase.credits,
          price_usd: pendingPurchase.price
        })
      })

      const data = await response.json()

      if (data.success && data.status === 'succeeded') {
        setShowPurchaseDialog(false)
        setPendingPurchase(null)
        toast.success(t('subscription.creditPacks.purchaseSuccess'))
        router.reload()
      } else if (data.success && data.status === 'requires_action') {
        setPurchaseError(t('subscription.creditPacks.requires3dSecure'))
        setIsPurchasing(false)
      } else {
        const errorMessage = data.errors?.join(', ') || data.error || t('subscription.creditPacks.purchaseFailed')
        setPurchaseError(errorMessage)
        setIsPurchasing(false)
      }
    } catch (error) {
      console.error('Error purchasing credits:', error)
      setPurchaseError(t('subscription.creditPacks.purchaseFailed'))
      setIsPurchasing(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleCancelSubscription = () => {
    setIsCanceling(true)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/subscription/cancel'

    // Add _method hidden field for DELETE request
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'DELETE'
    form.appendChild(methodInput)

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleReactivateSubscription = () => {
    setIsReactivating(true)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/billing/subscription/reactivate'

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const enterpriseFeatureKeys = [
    'sso', 'rbac', 'compliance', 'vpc', 'onPremise', 'sla', 'support', 'creditAllocation',
  ] as const

  const EnterpriseCard = () => (
    <div className="rounded-xl border-2 border-border p-6 flex flex-col hover:shadow-sm transition-shadow">
      <h3 className="text-lg font-bold">{t('subscription.enterprise.title')}</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-5">{t('subscription.enterprise.includesPlus')}</p>

      <div className="mb-5">
        <span className="text-3xl font-bold tracking-tight">{t('subscription.enterprise.pricing')}</span>
        <span className="text-muted-foreground text-sm ml-1">{t('subscription.enterprise.pricingSubtitle')}</span>
      </div>

      <div className="flex-1 mb-6 space-y-2.5">
        {enterpriseFeatureKeys.map((key) => (
          <div key={key} className="flex items-start gap-2 text-sm">
            <CheckIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span>{t(`subscription.enterprise.features.${key}`)}</span>
          </div>
        ))}
      </div>

      <Button variant="outline" className="w-full" asChild>
        <a href="mailto:contact@cayu.ai">
          {t('subscription.enterprise.contactSales')}
        </a>
      </Button>
    </div>
  )

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col">
          <div className="px-4 py-6 lg:px-6">
            <div className="mx-auto max-w-4xl space-y-8">

              {/* Current Plan Banner */}
              <div className="flex items-center justify-between rounded-xl border p-6">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">{t('subscription.currentPlan.title')}</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold tracking-tight">${organization.plan_price}</span>
                    <span className="text-sm text-muted-foreground">
                      {subscription?.billing_interval === 'year'
                        ? t('subscription.upgrade.perYear')
                        : t('subscription.upgrade.perMonth')}
                    </span>
                  </div>
                  {subscription && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                      <CalendarIcon className="h-3 w-3" />
                      <span>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant={isFreePlan ? 'secondary' : 'default'} className="text-base px-4 py-1.5">
                    {organization.plan_display_name}
                  </Badge>
                  {subscription && (
                    <Badge variant={subscription.status === 'active' ? 'outline' : 'secondary'} className="text-xs">
                      {subscription.status}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Subscription alerts */}
              {subscription?.cancel_at_period_end && (
                <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                    {t('subscription.currentPlan.cancelScheduled', { date: formatDate(subscription.current_period_end) })}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReactivateSubscription}
                    disabled={isReactivating}
                    className="flex-shrink-0"
                  >
                    {isReactivating ? t('subscription.currentPlan.reactivating') : t('subscription.currentPlan.keepSubscription')}
                  </Button>
                </div>
              )}

              {subscription?.pending_downgrade_price_id && (
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                    {t('subscription.currentPlan.pendingDowngrade', {
                      planName: subscription.pending_downgrade_display_name,
                      date: formatDate(subscription.pending_downgrade_at!)
                    })}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => {
                      const form = document.createElement('form')
                      form.method = 'POST'
                      form.action = '/billing/subscription/cancel_downgrade'
                      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                      if (csrfToken) {
                        const csrfInput = document.createElement('input')
                        csrfInput.type = 'hidden'
                        csrfInput.name = 'authenticity_token'
                        csrfInput.value = csrfToken
                        form.appendChild(csrfInput)
                      }
                      document.body.appendChild(form)
                      form.submit()
                    }}
                  >
                    {t('subscription.currentPlan.cancelDowngrade')}
                  </Button>
                </div>
              )}

              {/* Access Code Banner */}
              {active_access_code && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-4">
                  <div className="flex items-center gap-3">
                    <TicketIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-900 dark:text-emerald-200">
                        {t('subscription.accessCode.activeBanner.title')}
                      </p>
                      <p className="text-sm text-emerald-700 dark:text-emerald-300">
                        {t('subscription.accessCode.activeBanner.message', {
                          plan: active_access_code.granted_plan.charAt(0).toUpperCase() + active_access_code.granted_plan.slice(1),
                          code: active_access_code.code,
                          date: new Date(active_access_code.expires_at).toLocaleString(),
                        })}
                        {active_access_code.bonus_credits_granted > 0 && (
                          <span className="ml-2 font-medium">
                            {t('subscription.accessCode.activeBanner.bonusCredits', { credits: active_access_code.bonus_credits_granted })}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Credits Usage */}
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('subscription.credits.title')}</h3>
                  <Badge
                    variant={organization.credits_remaining > 0 ? 'default' : 'destructive'}
                    className="text-sm px-3 py-1"
                  >
                    {t('subscription.credits.remaining', { amount: organization.credits_remaining.toFixed(2) })}
                  </Badge>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">{t('subscription.credits.usage')}</span>
                    <span className="font-medium tabular-nums">
                      {t('subscription.credits.usageFormat', { used: organization.credits_used.toFixed(2), total: organization.credits_total.toFixed(2) })}
                    </span>
                  </div>
                  <Progress value={creditsUsagePercentage} className="h-2" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border px-4 py-3">
                    <p className="text-xs text-muted-foreground mb-1">{t('subscription.credits.planCredits')}</p>
                    <p className="text-lg font-semibold tabular-nums">{organization.plan_credits}</p>
                  </div>
                  <div className="rounded-lg border px-4 py-3">
                    <p className="text-xs text-muted-foreground mb-1">{t('subscription.credits.purchasedCredits')}</p>
                    <p className="text-lg font-semibold tabular-nums">{organization.credits_purchased.toFixed(2)}</p>
                  </div>
                  <div className="rounded-lg border px-4 py-3">
                    <p className="text-xs text-muted-foreground mb-1">{t('subscription.credits.creditsUsed')}</p>
                    <p className="text-lg font-semibold tabular-nums">{organization.credits_used.toFixed(2)}</p>
                  </div>
                  {isFreePlan && organization.usage_reset_at && (
                    <div className="rounded-lg border px-4 py-3">
                      <p className="text-xs text-muted-foreground mb-1">{t('subscription.credits.resetsOn')}</p>
                      <p className="text-sm font-semibold">{formatDate(organization.usage_reset_at)}</p>
                    </div>
                  )}
                </div>

                {/* Warnings */}
                {organization.credits_remaining < 10 && organization.credits_remaining > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <p className="text-sm text-orange-800 dark:text-orange-200">{t('subscription.credits.lowWarning')}</p>
                  </div>
                )}
                {organization.credits_remaining === 0 && (
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                      {t('subscription.credits.outOfCredits')}{' '}
                      {isFreePlan ? t('subscription.credits.upgradeFromFree') : t('subscription.credits.upgradeGeneric')}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => router.visit('/organization/credit_transactions')}
                  >
                    {t('subscription.credits.viewHistory')}
                  </Button>
                </div>
              </div>

              {/* Purchase Credits */}
              {organization.can_purchase_credits && !active_access_code && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('subscription.creditPacks.title')}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{t('subscription.creditPacks.note')}</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {credit_packs.map((pack) => (
                        <div
                          key={pack.credits}
                          className="border rounded-lg p-4 flex flex-col items-center hover:border-primary hover:shadow-sm transition-all cursor-pointer group"
                          onClick={() => handlePurchaseCredits(pack)}
                        >
                          <p className="text-2xl font-bold tabular-nums group-hover:text-primary transition-colors">{pack.credits}</p>
                          <p className="text-xs text-muted-foreground mb-3">{t('subscription.creditPacks.credits')}</p>
                          <p className="text-sm font-semibold">${pack.price.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Subscription Actions */}
              {subscription && (
                <>
                  <Separator />
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const form = document.createElement('form')
                        form.method = 'POST'
                        form.action = '/billing/portal'
                        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                        if (csrfToken) {
                          const csrfInput = document.createElement('input')
                          csrfInput.type = 'hidden'
                          csrfInput.name = 'authenticity_token'
                          csrfInput.value = csrfToken
                          form.appendChild(csrfInput)
                        }
                        document.body.appendChild(form)
                        form.submit()
                      }}
                    >
                      <CreditCardIcon className="h-4 w-4 mr-2" />
                      {t('subscription.currentPlan.manageBilling')}
                    </Button>
                    {!subscription.cancel_at_period_end && !subscription.pending_downgrade_price_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        {t('subscription.currentPlan.cancelSubscription')}
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Available Plans — Free users */}
              {isFreePlan && (
                <>
                  <Separator />
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('subscription.upgrade.title')}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{t('subscription.upgrade.description')}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      {availableTiers.map((tierKey) => {
                        const tierData = plan_variations[tierKey]
                        const selectedVariation = getSelectedVariation(tierKey)
                        const variations = getVariationsForInterval(tierKey)

                        return (
                          <div
                            key={tierKey}
                            className={`rounded-xl border-2 p-6 flex flex-col transition-shadow ${
                              tierKey === 'team' ? 'border-primary shadow-md' : 'border-border hover:shadow-sm'
                            }`}
                          >
                            {tierKey === 'team' && (
                              <Badge className="mb-3 w-fit">{t('subscription.upgrade.mostPopular')}</Badge>
                            )}
                            <h3 className="text-lg font-bold">{tierData.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1 mb-5">{tierData.description}</p>

                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={billingInterval === 'year'}
                                  onCheckedChange={(checked) => handleBillingIntervalChange(checked ? 'year' : 'month')}
                                />
                                <span className="text-xs">{t('subscription.billingInterval.annual')}</span>
                              </div>
                              {billingInterval === 'year' && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                  {t('subscription.billingInterval.save', { amount: calculateSavings(tierKey) })}
                                </Badge>
                              )}
                            </div>

                            <div className="mb-4">
                              <label className="text-xs text-muted-foreground mb-1.5 block">{t('subscription.upgrade.selectCredits')}</label>
                              <Select
                                value={selectedVariations[tierKey]}
                                onValueChange={(value) => handleVariationChange(tierKey, value)}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {variations.map((variation) => (
                                    <SelectItem key={variation.stripe_price_id} value={variation.stripe_price_id}>
                                      {variation.credits} credits/mo - ${variation.monthly_price}/mo
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="mb-5">
                              <span className="text-3xl font-bold tracking-tight">${selectedVariation?.price || 0}</span>
                              <span className="text-muted-foreground text-sm ml-1">
                                {billingInterval === 'year' ? t('subscription.upgrade.perYear') : t('subscription.upgrade.perMonth')}
                              </span>
                            </div>

                            <div className="flex-1 mb-6 space-y-2.5">
                              {[
                                <strong key="ai">{t('subscription.upgrade.features.aiCredits', { credits: selectedVariation?.credits || 0 })}</strong>,
                                t('subscription.upgrade.features.purchaseCredits'),
                                t('subscription.upgrade.features.privateHosting'),
                                t('subscription.upgrade.features.fullAccess'),
                              ].map((feature, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                  <CheckIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>

                            <Button
                              onClick={() => handleSubscribe(tierKey)}
                              className="w-full"
                              variant={tierKey === 'team' ? 'default' : 'outline'}
                            >
                              {t('subscription.upgrade.subscribeTo', { planName: tierData.name })}
                            </Button>
                          </div>
                        )
                      })}
                      <EnterpriseCard />
                    </div>
                  </div>
                </>
              )}

              {/* Change Plan — Active subscribers */}
              {hasActiveSubscription && !subscription.cancel_at_period_end && (
                <>
                  <Separator />
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('subscription.changePlan.title')}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{t('subscription.changePlan.description')}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      {availableTiers.map((tierKey) => {
                        const tierData = plan_variations[tierKey]
                        const selectedVariation = getSelectedVariation(tierKey)
                        const variations = getVariationsForInterval(tierKey)
                        const isCurrentTier = subscription.plan_name === tierKey
                        const isCurrentVariation = subscription.stripe_price_id === selectedVariations[tierKey]

                        return (
                          <div
                            key={tierKey}
                            className={`rounded-xl border-2 p-6 flex flex-col transition-shadow ${
                              isCurrentTier
                                ? 'border-primary bg-primary/5'
                                : tierKey === 'team'
                                ? 'border-primary/50 hover:shadow-sm'
                                : 'border-border hover:shadow-sm'
                            }`}
                          >
                            <div className="flex gap-2 mb-3">
                              {isCurrentTier && (
                                <Badge variant="default">{t('subscription.changePlan.currentTier')}</Badge>
                              )}
                              {tierKey === 'team' && !isCurrentTier && (
                                <Badge variant="outline">{t('subscription.upgrade.mostPopular')}</Badge>
                              )}
                            </div>
                            <h3 className="text-lg font-bold">{tierData.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1 mb-5">{tierData.description}</p>

                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={billingInterval === 'year'}
                                  onCheckedChange={(checked) => handleBillingIntervalChange(checked ? 'year' : 'month')}
                                />
                                <span className="text-xs">{t('subscription.billingInterval.annual')}</span>
                              </div>
                              {billingInterval === 'year' && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                  {t('subscription.billingInterval.save', { amount: calculateSavings(tierKey) })}
                                </Badge>
                              )}
                            </div>

                            <div className="mb-4">
                              <label className="text-xs text-muted-foreground mb-1.5 block">{t('subscription.upgrade.selectCredits')}</label>
                              <Select
                                value={selectedVariations[tierKey]}
                                onValueChange={(value) => handleVariationChange(tierKey, value)}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {variations.map((variation) => (
                                    <SelectItem key={variation.stripe_price_id} value={variation.stripe_price_id}>
                                      {variation.credits} credits/mo - ${variation.monthly_price}/mo
                                      {subscription.stripe_price_id === variation.stripe_price_id && ` ${t('subscription.changePlan.current')}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="mb-5">
                              <span className="text-3xl font-bold tracking-tight">${selectedVariation?.price || 0}</span>
                              <span className="text-muted-foreground text-sm ml-1">
                                {billingInterval === 'year' ? t('subscription.upgrade.perYear') : t('subscription.upgrade.perMonth')}
                              </span>
                            </div>

                            <div className="flex-1 mb-6 space-y-2.5">
                              {[
                                <strong key="ai">{t('subscription.upgrade.features.aiCredits', { credits: selectedVariation?.credits || 0 })}</strong>,
                                t('subscription.upgrade.features.purchaseCredits'),
                                t('subscription.upgrade.features.privateHosting'),
                                t('subscription.upgrade.features.fullAccess'),
                              ].map((feature, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                  <CheckIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>

                            <Button
                              onClick={() => handleChangePlan(tierKey)}
                              className="w-full"
                              variant={isCurrentTier ? 'outline' : tierKey === 'team' ? 'default' : 'outline'}
                              disabled={isCurrentVariation}
                            >
                              {isCurrentVariation ? t('subscription.changePlan.currentPlan') : t('subscription.changePlan.changePlan')}
                            </Button>
                          </div>
                        )
                      })}
                      <EnterpriseCard />
                    </div>
                  </div>
                </>
              )}

              {/* Access Code Redemption */}
              {!hasActiveSubscription && !active_access_code && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('subscription.accessCode.title')}</h3>
                    <p className="text-sm text-muted-foreground">{t('subscription.accessCode.description')}</p>
                    <div className="flex gap-2 max-w-md">
                      <Input
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                        placeholder={t('subscription.accessCode.placeholder')}
                        className="font-mono"
                        onKeyDown={(e) => e.key === 'Enter' && handleRedeemAccessCode()}
                      />
                      <Button
                        onClick={handleRedeemAccessCode}
                        disabled={isRedeeming || !accessCode.trim()}
                      >
                        {isRedeeming ? t('subscription.accessCode.redeeming') : t('subscription.accessCode.redeem')}
                      </Button>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('subscription.confirmDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                if (!pendingTierChange || !subscription) return t('subscription.confirmDialog.defaultMessage')

                const newVariation = getSelectedVariation(pendingTierChange)
                // Compare against actual current subscription price, not dropdown selection
                const isDowngrade = newVariation && newVariation.price < organization.plan_price

                if (isDowngrade) {
                  return t('subscription.confirmDialog.downgradeMessage', {
                    planName: newVariation.display_name,
                    date: formatDate(subscription.current_period_end)
                  })
                }
                return t('subscription.confirmDialog.upgradeMessage')
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowConfirmDialog(false)
              setPendingTierChange(null)
            }}>
              {t('subscription.confirmDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmChangePlan}>
              {t('subscription.confirmDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('subscription.cancelDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {subscription ? (
                <>
                  {t('subscription.cancelDialog.message', { date: formatDate(subscription.current_period_end) })}
                  <br /><br />
                  {t('subscription.cancelDialog.reactivateNote')}
                </>
              ) : (
                t('subscription.cancelDialog.defaultMessage')
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowCancelDialog(false)}>
              {t('subscription.cancelDialog.keepSubscription')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowCancelDialog(false)
                handleCancelSubscription()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isCanceling}
            >
              {isCanceling ? t('subscription.cancelDialog.canceling') : t('subscription.cancelDialog.yesCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credit Pack Purchase Confirmation Dialog */}
      <AlertDialog open={showPurchaseDialog} onOpenChange={(open) => {
        if (!isPurchasing) {
          setShowPurchaseDialog(open)
          if (!open) {
            setPendingPurchase(null)
            setPurchaseError(null)
          }
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('subscription.creditPacks.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {pendingPurchase && t('subscription.creditPacks.confirmMessage', {
                  credits: pendingPurchase.credits,
                  price: pendingPurchase.price.toFixed(2)
                })}
                {purchaseError && (
                  <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
                    {purchaseError}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPurchasing}>
              {t('common:buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmPurchaseCredits()
              }}
              disabled={isPurchasing}
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('subscription.creditPacks.purchasing')}
                </>
              ) : (
                t('subscription.creditPacks.confirmPurchase', { price: pendingPurchase?.price.toFixed(2) || '0' })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

Subscription.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Subscription
